import { useState, type ReactNode } from 'react'

import { Icon } from './Icon'
import { cn } from '../lib/cn'
import type { ImageAsset } from '../lib/imageAssets'

/**
 * Either the picture means something and carries a description, or it is
 * decorative and says so out loud. There is no third option — the type system
 * refuses an image whose description nobody decided on, because "forgot to
 * write alt text" and "deliberately silent" look identical once shipped.
 */
type Description =
  | { alt: string; decorative?: undefined }
  | { decorative: true; alt?: undefined }

export type ImageProps = {
  asset: ImageAsset
  /** Classes for the frame. It reserves the space, so size it here. */
  className?: string
  /** Above-the-fold only — everything else stays lazy. */
  eager?: boolean
  /**
   * A fixed proportion for the frame, e.g. `"16 / 9"`. Absent, the frame takes
   * the photograph's own proportion, as before. Set it where several photos sit
   * side by side and must be one shape: the photo is cropped to fill, centred.
   * An inline style, so it must be given here -- a Tailwind `aspect-*` class on
   * `className` loses to the inline default.
   */
  aspectRatio?: string
} & Description

type LoadState = 'loading' | 'loaded' | 'failed'

/**
 * The one place the app renders a photograph (M17-T18a). Handles the loading
 * state, the failed state, the description, and the reserved space — so no
 * page has to remember four things every time it shows a picture.
 */
export function Image({
  asset,
  className,
  eager,
  alt,
  decorative,
  aspectRatio,
}: ImageProps): ReactNode {
  const [state, setState] = useState<LoadState>('loading')

  // Reserving the box from the emitted dimensions is what stops the page
  // jumping as pictures arrive — the placeholder occupies the same space.
  const frame = (
    <div
      className={cn(
        'relative overflow-hidden bg-gradient-to-br from-bg-soft to-bg-sunken',
        className,
      )}
      style={{ aspectRatio: aspectRatio ?? `${asset.width} / ${asset.height}` }}
    >
      {state === 'failed' ? (
        // The neutral placeholder the cards already use — never a broken-image
        // icon, never a hole where the picture was.
        <div
          className="grid h-full w-full place-items-center text-ink-4"
          {...(decorative
            ? { 'aria-hidden': true }
            : { role: 'img', 'aria-label': alt })}
          data-testid="image-placeholder"
        >
          <Icon name="image" size={26} />
        </div>
      ) : (
        <picture className="block h-full w-full">
          <source type="image/webp" srcSet={asset.webp} />
          <img
            src={asset.fallback}
            width={asset.width}
            height={asset.height}
            alt={decorative ? '' : alt}
            {...(decorative ? { 'aria-hidden': true } : {})}
            loading={eager ? 'eager' : 'lazy'}
            decoding="async"
            onLoad={() => setState('loaded')}
            onError={() => setState('failed')}
            className={cn(
              'h-full w-full object-cover object-center transition-opacity duration-200',
              state === 'loading' ? 'opacity-0' : 'opacity-100',
            )}
          />
        </picture>
      )}
    </div>
  )

  return frame
}
